import styled from '@emotion/styled';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { NodeResizer, NodeToolbar, Position } from '@xyflow/react';

import React, { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import { useDarkMode } from 'src/context/DarkModeContext';
import { HexColorPicker } from 'react-colorful';
import SimpleDropdown from 'src/components/ui/SimpleDropdown';
import { colorWithAlpha } from 'src/styles/bootstrap/scss/_functions';
import classnames from 'src/util/classnames';
import { NOTE_H, NOTE_W } from '..';
import Icon from 'src/components/Icon';

export default function NoteNode({ id, data, selected, height, width }) {
  // const { state, dispatchAction } = useContext(WorkflowContext);
  const textAreaRef = useRef();
  const [editable, setEditable] = useState(false);
  const [colorPickerOpenFor, setColorPickerOpenFor] = useState();
  const { isDarkMode } = useDarkMode();

  const { bgColor = '#ffffff', color = '#000', isBold, isItalics, fontSize = 13, text, onChange, onDelete } = data;

  useEffect(() => {
    if (!selected) {
      setEditable(false);
      setColorPickerOpenFor(undefined);
    } else if (editable && textAreaRef.current) {
      textAreaRef.current.focus();
      textAreaRef.current.setSelectionRange(text?.length || 0, text?.length || 0);
    }
  }, [selected, editable, text]);

  return (
    <StyledNoteNode selected={selected} height={height} width={width} bgColor={bgColor} isDarkMode={isDarkMode}>
      <NodeResizer
        color="rgb(19, 117, 237)"
        isVisible={selected}
        minWidth={40}
        minHeight={40}
        onResize={(e, params) => onChange(id, { width: params.width, height: params.height })}
      />
      <NodeToolbar isVisible={selected} position={Position.Top}>
        {colorPickerOpenFor && (
          <HexColorPicker
            className="nodrag nopan"
            color={data[colorPickerOpenFor]}
            onChange={newColor => onChange(id, { [colorPickerOpenFor]: newColor })}
          />
        )}
        <ButtonContainer>
          <button className={classnames('xy-theme__button', { 'is-active': editable })} onClick={() => setEditable(x => !x)}>
            <Icon icon="Pencil" />
          </button>

          <div className="xy-theme__button-group">
            {/* <button className="xy-theme__button">font</button> */}
            <SimpleDropdown
              toggleStyle={{ width: 'fit-content', padding: 4 }}
              color="none"
              direction="up"
              noCaret
              value={fontSize}
              options={FONT_OPTIONS}
              onSelect={value => onChange(id, { fontSize: value })}
              placeholder={<Trans>Size</Trans>}
            />
            <button className={classnames('xy-theme__button', { 'is-active': isBold })} onClick={() => onChange(id, { isBold: !isBold })}>
              <b>b</b>
            </button>
            <button className={classnames('xy-theme__button', { 'is-active': isItalics })} onClick={() => onChange(id, { isItalics: !isItalics })}>
              <i>i</i>
            </button>
          </div>

          <button
            className="xy-theme__button fade-to-color"
            style={{ '--fade-color': '#d9534f' }}
            onClick={e => {
              onDelete?.(id);
            }}
          >
            delete
          </button>
          <button
            className="xy-theme__button"
            style={{ backgroundColor: color, width: '1rem', height: '1rem', borderRadius: '100%' }}
            onClick={() => setColorPickerOpenFor(x => (x === 'color' ? undefined : 'color'))}
          />
          <button
            className="xy-theme__button"
            style={{ backgroundColor: bgColor, width: '1rem', height: '1rem', borderRadius: '100%' }}
            onClick={() => setColorPickerOpenFor(x => (x === 'bgColor' ? undefined : 'bgColor'))}
          />
        </ButtonContainer>
      </NodeToolbar>
      <TextArea
        ref={textAreaRef}
        editable={editable}
        value={text}
        onChange={value => onChange(id, { text: value })}
        isBold={isBold}
        isItalics={isItalics}
        bgColor={bgColor}
        color={color}
        fontSize={fontSize}
      />
      <div
        style={{
          display: editable ? 'none' : 'block',
          width: '100%',
          minHeight: 20,
          padding: 8,
          color,
          fontWeight: isBold ? 'bold' : 'normal',
          fontStyle: isItalics ? 'italic' : 'normal',
          outline: 'none',
          fontSize: `${fontSize ?? 13}px`,
          lineHeight: 1.4,
          resize: 'none',
        }}
      >
        {text || (
          <em>
            <Trans>(New note)</Trans>
          </em>
        )}
      </div>
    </StyledNoteNode>
  );
}

const TextArea = forwardRef(function TextArea({ value, onChange, isBold, isItalics, color, bgColor, fontSize, editable }, ref) {
  return (
    <div
      className="nodrag nopan"
      style={{ display: editable ? 'block' : 'none' }}
      onPointerDown={e => e.stopPropagation()}
      onMouseDown={e => e.stopPropagation()}
      onTouchStart={e => e.stopPropagation()}
    >
      <textarea
        ref={ref}
        className="nodrag nopan note-node-textarea"
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        onPointerDown={e => e.stopPropagation()}
        onMouseDown={e => e.stopPropagation()}
        onTouchStart={e => e.stopPropagation()}
        style={{
          width: '100%',
          minHeight: 20,
          padding: 8,
          color,
          background: 'transparent',
          fontWeight: isBold ? 'bold' : 'normal',
          fontStyle: isItalics ? 'italic' : 'normal',
          outline: 'none',
          fontSize: `${fontSize ?? 13}px`,
          lineHeight: 1.4,
          resize: 'none',
        }}
        placeholder={t`Enter a workflow note...`}
      />
    </div>
  );
});

const ButtonContainer = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginTop: '0.7rem',
});

const StyledNoteNode = styled('div')(({ selected, height, width, bgColor, isDarkMode }) => ({
  backgroundColor: bgColor,
  // background: selected ? 'linear-gradient(180deg,#fdfdfd, #f3f4f6)' : 'linear-gradient(180deg,#ffffff,#f7f7f9)',
  // border: selected ? '2px solid #cfd4dc' : '1px solid #e5e7eb',
  border: selected ? '2px solid rgb(19, 117, 237)' : `2px solid ${isDarkMode ? '#40444f' : '#e5e7eb'}`,
  // borderRadius: 12,
  // boxShadow: selected ? '0 6px 20px rgba(0,0,0,0.12)' : '0 4px 14px rgba(0,0,0,0.08)',
  boxShadow: selected
    ? 'rgba(19, 117, 237, 0.16) 0px 1px 2px, rgb(19, 117, 237) 0px 0px 0px 2px, rgba(19, 117, 237, 0.08) 0px 3px 6px, rgba(19, 117, 237, 0.5) 0px 0px 0px 6px'
    : 'none',

  // padding: 12,
  width: width,
  height: height,
  cursor: 'pointer',
  position: 'relative',
}));

const FONT_OPTIONS = [10, 11, 12, 13, 14, 15, 16].map(x => ({ value: x, title: x }));
